/**
 * Wecrew Copilot AI API client — talks to FastAPI backend at /api/v1
 * Dev: Vite proxies /api → http://localhost:8000
 */

import { getItem, removeItem, setItem } from "./safeStorage";

const API_BASE = import.meta.env.VITE_API_BASE || "/api/v1";
const TOKEN_KEY = "argusai_token";
const SESSION_KEY = "wecrew_session";
export const AUTH_EXPIRED_EVENT = "argusai:session-expired";

export function getToken() {
  return getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) setItem(TOKEN_KEY, token);
  else removeItem(TOKEN_KEY);
}

/** Clear JWT + demo session and notify App to redirect to sign-in. */
export function clearAuthSession() {
  removeItem(TOKEN_KEY);
  removeItem(SESSION_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
}

function handleUnauthorizedResponse(path) {
  const isAuthRoute =
    path === "/auth/login" ||
    path === "/auth/register" ||
    path.startsWith("/auth/login") ||
    path.startsWith("/auth/register");
  if (isAuthRoute) return;
  if (getToken()) clearAuthSession();
}

/** Turn FastAPI validation arrays into a short user-facing message. */
export function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => (item && typeof item.msg === "string" ? item.msg : null))
      .filter(Boolean);
    if (msgs.length) return msgs.join(". ");
  }
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return "Request failed";
    }
  }
  return "Request failed";
}

async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401) handleUnauthorizedResponse(path);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail || JSON.stringify(body);
    } catch {
      /* ignore */
    }
    throw new Error(formatApiError(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  health: () => fetch("/health").then((r) => r.json()).catch(() =>
    fetch(`${API_BASE.replace(/\/api\/v1$/, "")}/health`).then((r) => r.json())
  ),

  register: async (email, password, full_name) => {
    const data = await request("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name: full_name, full_name }),
    });
    if (data?.access_token) setToken(data.access_token);
    return data;
  },

  login: async (email, password) => {
    const data = await request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (data?.access_token) setToken(data.access_token);
    return data;
  },

  me: () => request("/auth/me"),

  listKnowledgeBases: () => request("/knowledge-bases"),

  createKnowledgeBase: (payload) =>
    request("/knowledge-bases", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listDocuments: (kb_id) =>
    request(kb_id ? `/documents?kb_id=${encodeURIComponent(kb_id)}` : "/documents"),

  chatQuery: ({ query, kb_ids = [], top_k = 5, conversation_history = [], log_source, log_search }) =>
    request("/chat/query", {
      method: "POST",
      body: JSON.stringify({
        query,
        kb_ids,
        top_k,
        conversation_history,
        ...(log_source ? { log_source, log_search: log_search || query } : {}),
      }),
    }),

  /**
   * Stream RAG tokens via SSE (POST /chat/stream).
   */
  chatStream: async ({
    query,
    kb_ids = [],
    top_k = 5,
    conversation_history = [],
    log_source,
    log_search,
    onToken,
    onSources,
    onLogs,
    onDone,
  }) => {
    const headers = { "Content-Type": "application/json" };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const body = {
      query,
      kb_ids,
      top_k,
      conversation_history,
      ...(log_source ? { log_source, log_search: log_search || query } : {}),
    };

    const res = await fetch(`${API_BASE}/chat/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (res.status === 401) handleUnauthorizedResponse("/chat/stream");
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const body = await res.json();
        detail = body.detail || JSON.stringify(body);
      } catch {
        /* ignore */
      }
      throw new Error(formatApiError(detail));
    }

    const reader = res.body?.getReader();
    if (!reader) throw new Error("Streaming not supported");

    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        let payload;
        try {
          payload = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (payload.type === "error") {
          throw new Error(payload.message || "LLM request failed");
        }
        if (payload.type === "token" && payload.text) onToken?.(payload.text);
        if (payload.type === "sources") onSources?.(payload.sources);
        if (payload.type === "logs") onLogs?.(payload.log_evidence);
        if (payload.type === "done") onDone?.(payload);
      }
    }
  },

  logsStatus: () => request("/logs/status"),

  logsQuery: ({ source, search, limit = 100 }) =>
    request("/logs/query", {
      method: "POST",
      body: JSON.stringify({ source, search, limit }),
    }),

  analytics: () => request("/analytics"),

  listApiKeys: () => request("/api-keys"),

  createApiKey: (name, expires_days = null) =>
    request("/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, expires_days }),
    }),

  revokeApiKey: (key_id) =>
    request(`/api-keys/${encodeURIComponent(key_id)}`, { method: "DELETE" }),

  listIntegrations: () => request("/integrations"),

  connectIntegration: ({ catalog_id, name, env_keys_set = [], note = "" }) =>
    request("/integrations/connect", {
      method: "POST",
      body: JSON.stringify({ catalog_id, name, env_keys_set, note }),
    }),

  testIntegration: (catalog_id) =>
    request(`/integrations/${encodeURIComponent(catalog_id)}/test`, { method: "POST" }),

  disconnectIntegration: (catalog_id) =>
    request(`/integrations/${encodeURIComponent(catalog_id)}`, { method: "DELETE" }),

  uploadDocument: async ({ file, kb_id }) => {
    const form = new FormData();
    form.append("file", file);
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    // #region agent log
    fetch("http://127.0.0.1:7327/ingest/4d0efa08-02dc-4fb2-b21a-303fa0b4e23a", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "d84fcd" },
      body: JSON.stringify({
        sessionId: "d84fcd",
        runId: "upload-pre",
        hypothesisId: "H2-H3",
        location: "api.js:uploadDocument:pre",
        message: "upload request start",
        data: {
          apiBase: API_BASE,
          kb_id,
          fileName: file?.name,
          fileSize: file?.size,
          ext: file?.name?.split(".").pop()?.toLowerCase(),
          hasToken: Boolean(token),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    const res = await fetch(`${API_BASE}/documents/upload?kb_id=${encodeURIComponent(kb_id)}`, {
      method: "POST",
      headers,
      body: form,
    });
    // #region agent log
    fetch("http://127.0.0.1:7327/ingest/4d0efa08-02dc-4fb2-b21a-303fa0b4e23a", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "d84fcd" },
      body: JSON.stringify({
        sessionId: "d84fcd",
        runId: "upload-pre",
        hypothesisId: "H2-H4",
        location: "api.js:uploadDocument:response",
        message: "upload response",
        data: { status: res.status, ok: res.ok },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    if (res.status === 401) handleUnauthorizedResponse("/documents/upload");
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const body = await res.json();
        detail = body.detail || JSON.stringify(body);
      } catch {
        /* ignore */
      }
      throw new Error(formatApiError(detail));
    }
    return res.json();
  },
};
